import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Spinner,
  SimpleGrid,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import ProductApi from "api/product";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import { FaListUl, FaGamepad, FaTimes } from "react-icons/fa";
import { getIsoDateWithoutTime, toJalaliString } from "utils/helper";

const platformColor = {
  ps4: "blue.500",
  ps5: "purple.500",
};

const getDates = () => {
  return Array.from({ length: 6 }, (_, i) => {
    const isoDate = getIsoDateWithoutTime(i - 3);
    const label =
      i === 0
        ? "۳ روز قبل"
        : i === 1
        ? "۲ روز قبل"
        : i === 2
        ? "دیروز"
        : i === 3
        ? "امروز"
        : i === 4
        ? "فردا"
        : "۲ روز بعد";

    return {
      label,
      date: isoDate,
      fullLabel: `${label}\n${toJalaliString(isoDate)}`, // استفاده از تابع جدید
    };
  });
};


const DailyList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeDate, setActiveDate] = useState(getDates()[3].date);
  const textColor = useColorModeValue("gray.700", "white");

  const fetchDailyList = async (date) => {
    setLoading(true);
    try {
      const response = await ProductApi.DailyList(date);
      setProducts(response.data);
    } catch (err) {
      setError("خطا در دریافت اطلاعات");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyList(activeDate);
  }, [activeDate]);

  const dates = getDates();

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card>
        <CardHeader
          p="6px 0px 22px 0px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex align="center" gap={2}>
            <FaListUl size={20} />
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              لیست روزانه محصولات
            </Text>
          </Flex>
        </CardHeader>

        <Box px={6}>
          <Tabs
            isFitted
            variant="soft-rounded"
            colorScheme="orange"
            onChange={(index) => setActiveDate(dates[index].date)}
            defaultIndex={3}
          >
            <TabList mb={4} overflowX="auto">
              {dates.map((d, idx) => (
                <Tab
                  key={idx}
                  whiteSpace="pre-line"
                  fontSize="md"
                  px={6}
                  py={3}
                  borderRadius="xl"
                  _selected={{
                    bg: "orange.400",
                    color: "white",
                    boxShadow: "0 0 8px rgba(255, 165, 0, 0.6)",
                  }}
                  _hover={{
                    bg: "orange.300",
                    color: "white",
                  }}
                  transition="all 0.3s ease"
                  cursor="pointer"
                  mx={1}
                  textAlign="center"
                >
                  {d.fullLabel}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {dates.map((d, idx) => (
                <TabPanel key={idx} px={0}>
                {loading ? (
                  <Spinner size="xl" />
                ) : error ? (
                  <Text color="red.500">{error}</Text>
                ) : products.length === 0 ? (
                  <Text textAlign="center" color="gray.500" fontSize="lg" py={10}>
                    هیچ محصولی برای این تاریخ یافت نشد.
                  </Text>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {products.map((product) => {
                      const ps4Attrs = product.attributes.filter(
                        (a) => a.platform === "ps4"
                      );
                      const ps5Attrs = product.attributes.filter(
                        (a) => a.platform === "ps5"
                      );
              
                      return (
                        <Box
                          key={product.id}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={4}
                          boxShadow="sm"
                          bg="white"
                        >
                          <Flex align="center" gap={2} mb={3}>
                            <FaGamepad size={18} color="gray.600" />
                            <Text fontSize="md" fontWeight="bold">
                              {product.name}
                            </Text>
                          </Flex>
              
                          <Flex justify="space-between" gap={4}>
                            <VStack align="stretch" spacing={2} flex={1}>
                              {ps4Attrs.map((attr, index) => (
                                <Box
                                  key={index}
                                  bg={platformColor[attr.platform]}
                                  color="white"
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  fontSize="sm"
                                  textAlign="center"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  style={{ direction: "ltr" }}
                                >
                                  <Text>{`Z${attr.z_type}PS4 - ${attr.price.toLocaleString()}T`}</Text>
                                  {attr.stock === 0 && (
                                    <FaTimes size={16} color="red" />
                                  )}
                                </Box>
                              ))}
                            </VStack>
              
                            <VStack align="stretch" spacing={2} flex={1}>
                              {ps5Attrs.map((attr, index) => (
                                <Box
                                  key={index}
                                  bg={platformColor[attr.platform]}
                                  color="white"
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  fontSize="sm"
                                  textAlign="center"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  style={{ direction: "ltr" }}
                                >
                                  <Text>{`Z${attr.z_type}PS5 - ${attr.price.toLocaleString()}T`}</Text>
                                  {attr.stock === 0 && (
                                    <FaTimes size={16} color="red" />
                                  )}
                                </Box>
                              ))}
                            </VStack>
                          </Flex>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                )}
              </TabPanel>
              
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </Card>
    </Flex>
  );
};

export default DailyList;
